import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for parallax effect with 3D depth perception
 * Creates layered movement based on cursor position
 * 
 * @param {boolean} enabled - Whether parallax is enabled
 * @param {number} intensity - Parallax intensity multiplier (default: 0.05)
 * @returns {Object} { parallaxStyle, backgroundParallax, foregroundParallax }
 */
export const useParallaxEffect = (enabled = true, intensity = 0.05) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [parallaxValues, setParallaxValues] = useState({
    background: { x: 0, y: 0 },
    midground: { x: 0, y: 0 },
    foreground: { x: 0, y: 0 }
  });
  const rafRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!enabled) return;

    const { clientX, clientY } = e;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Normalize mouse position to -1 to 1 range
    const normalizedX = (clientX / windowWidth - 0.5) * 2;
    const normalizedY = (clientY / windowHeight - 0.5) * 2;

    setMousePosition({ x: normalizedX, y: normalizedY });
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    // Use requestAnimationFrame for smooth parallax updates
    const updateParallax = () => {
      const { x, y } = mousePosition;

      // Different layers move at different speeds for 3D depth
      setParallaxValues({
        background: {
          x: x * intensity * 20, // Slowest (furthest away)
          y: y * intensity * 20
        },
        midground: {
          x: x * intensity * 40, // Medium speed
          y: y * intensity * 40
        },
        foreground: {
          x: x * intensity * 60, // Fastest (closest)
          y: y * intensity * 60
        }
      });
    };

    rafRef.current = requestAnimationFrame(updateParallax);

    return () => {
      if (rafRef?.current) {
        cancelAnimationFrame(rafRef?.current);
      }
    };
  }, [mousePosition, enabled, intensity]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [enabled, handleMouseMove]);

  // Helper function to generate transform style
  const getParallaxStyle = (layer = 'midground') => {
    if (!enabled) return {};

    const { x, y } = parallaxValues?.[layer] || { x: 0, y: 0 };
    return {
      transform: `translate3d(${x}px, ${y}px, 0)`,
      transition: 'transform 0.1s ease-out',
      willChange: 'transform'
    };
  };

  return {
    parallaxStyle: getParallaxStyle,
    backgroundParallax: getParallaxStyle('background'),
    midgroundParallax: getParallaxStyle('midground'),
    foregroundParallax: getParallaxStyle('foreground'),
    mousePosition
  };
};

export default useParallaxEffect;